import type { NavItems } from '@ksyos/design-system';
import { Button, Drawer, Header, IconButton, KsyosLogo, SideNav } from '@ksyos/design-system';
import { useState } from 'react';
import { RemoveScroll } from 'react-remove-scroll';
import { getSelectedLanguage } from '@frontend/translate';
import { useUiStateContext } from '@frontend/utils/hooks/useUiState';
import type { IOrganization } from '../../../interfaces/';
import type { EmploymentSwitch } from '../types/employmentSwitch';
import type { GlobalUserContext } from '../types/globalUserContext';
import type { UserEmployment } from '../types/userEmployment';
import { useHasActiveNavigationItem } from '../hooks/useHasActiveNavigationItem';
import { EmploymentSwitchModal } from './EmploymentSwitchModal';
import { UserNavigation } from './UserNavigation';

export type MobileHeaderProps = {
    currentOrganization: Pick<IOrganization, 'id' | 'name' | 'suppressEmployeePayoutOverview'>;
    employments: UserEmployment[];
    employmentSwitch: EmploymentSwitch | null;
    frontendSettings: { environment: string };
    globalUserContext: GlobalUserContext;
    navigationItems: NavItems[];
    paymentsUrl: string;
    profileUrl: string;
    setEmploymentSwitch: React.Dispatch<React.SetStateAction<EmploymentSwitch | null>>;
    userInformation: Partial<GlobalUserContext>;
};

function MobileNavigationItem({ item, onClose }: Readonly<{ item: NavItems; onClose: () => void }>) {
    const hasActiveChild = useHasActiveNavigationItem(item.items);

    if (item.items) {
        const { key, items, ...rest } = item;

        return (
            <SideNav.Group
                defaultOpen={hasActiveChild}
                key={key}
                {...rest}
            >
                {items.map((subItem: NavItems) => (
                    <MobileNavigationItem
                        item={subItem}
                        key={subItem.key}
                        onClose={onClose}
                    />
                ))}
            </SideNav.Group>
        );
    }

    const { key, onClick, ...rest } = item;

    return (
        <SideNav.Link
            key={key}
            onClick={(event: React.MouseEvent<HTMLAnchorElement> & React.MouseEvent<HTMLButtonElement>) => {
                onClose();
                if (onClick) {
                    onClick(event);
                }
            }}
            {...rest}
        />
    );
}

export function MobileHeader({
    navigationItems,
    currentOrganization,
    employments,
    globalUserContext,
    paymentsUrl,
    profileUrl,
    setEmploymentSwitch,
    userInformation,
    frontendSettings,
    employmentSwitch,
}: Readonly<MobileHeaderProps>) {
    const { dispatchUiStateAction } = useUiStateContext();
    const language = getSelectedLanguage();
    const [isOpen, setIsOpen] = useState(false);

    return (
        <Header sx={{ justifyContent: 'space-between' }}>
            <Header.Item>
                <IconButton
                    icon="menu"
                    iconColor="default"
                    onClick={() => setIsOpen(true)}
                />
            </Header.Item>
            <Header.Item sx={{ flexDirection: 'column', justifyContent: 'center' }}>
                <KsyosLogo
                    height="30px"
                    width="99px"
                />
                {frontendSettings.environment !== 'production' && (
                    <Header.EnvLabel sx={{ paddingRight: 0, textAlign: 'center', width: '100%' }}>
                        {frontendSettings.environment} omgeving
                    </Header.EnvLabel>
                )}
            </Header.Item>
            <Header.Item className={RemoveScroll.classNames.fullWidth}>
                <UserNavigation
                    currentOrganization={currentOrganization}
                    employments={employments}
                    globalUserContext={globalUserContext}
                    paymentsUrl={paymentsUrl}
                    profileUrl={profileUrl}
                    setEmploymentSwitch={setEmploymentSwitch}
                    userInformation={userInformation}
                />
            </Header.Item>
            <Drawer
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                position="left"
                size="xsmall"
            >
                <Drawer.Header sx={{ alignSelf: 'center' }}>
                    <KsyosLogo
                        height="30px"
                        width="99px"
                    />
                </Drawer.Header>
                <Drawer.CloseButton
                    onClick={() => setIsOpen(false)}
                    position="left"
                />
                <Drawer.Body>
                    <SideNav>
                        {navigationItems.map((item) => (
                            <MobileNavigationItem
                                item={item}
                                key={item.key}
                                onClose={() => setIsOpen(false)}
                            />
                        ))}
                    </SideNav>
                </Drawer.Body>
                <Drawer.Footer>
                    <Button
                        color="warning"
                        onClick={() => {
                            dispatchUiStateAction({ isModalOpen: true, type: 'setIsFeedbackModalOpen' });
                            setIsOpen(false);
                        }}
                        size="small"
                    >
                        {language.support.feedback}
                    </Button>
                    <Button
                        color="warning"
                        onClick={() => {
                            dispatchUiStateAction({ isModalOpen: true, type: 'setIsSupportModalOpen' });
                            setIsOpen(false);
                        }}
                        size="small"
                    >
                        {language.support.support}
                    </Button>
                </Drawer.Footer>
            </Drawer>
            <EmploymentSwitchModal
                currentOrganization={currentOrganization}
                employmentSwitch={employmentSwitch}
                setEmploymentSwitch={setEmploymentSwitch}
            />
        </Header>
    );
}
